/**
 * The /v1 contract for roles, per-person access, Reports to, invites, asks
 * and the primary admin (web-redesign-02 phase 4).
 *
 * Transport shapes only, beside the old team shapes in `team.ts`, which keep
 * their words and their routes for the phone already in people's hands
 * (Decided 12). Every rule these carry is enforced on the server; a screen
 * that hides a button is a courtesy.
 *
 * Every route here takes the workspace from `x-alluzo-tenant`, as the record
 * routes do. The words a person reads are the screen's: these carry keys,
 * ids and levels, never sentences, apart from the refusals the server says.
 */
import type {
  AccessLevel,
  EntryReaches,
  InviteDelivery,
  PermissionKey,
  Reach,
  RightAsk,
  RolePermissions,
  SystemRoleKey,
} from './roles';
import type { VisibleRecordKind } from './api';

/* ------------------------------------------------------------------ roles */

/** One role, as Roles lists it and the role page opens it. */
export type WorkspaceRole = {
  id: string;
  /** The workspace's own word: Admin, Member, Cashier. */
  name: string;
  /** What it is for, in the admin's words; null when nobody wrote one. */
  purpose: string | null;
  /** admin or member for the two system roles, null for one a workspace made. */
  systemKey: SystemRoleKey | null;
  permissions: RolePermissions;
  /** The same as `permissions.sees_new_records`, pulled out for the list. */
  seesNewRecords: boolean;
  /** A role that only looks: the badge is outlined, and an old phone is told viewer. */
  lookOnly: boolean;
  /** How many people hold it. */
  holders: number;
  madeBy: string | null;
  createdAt: string;
};

/** GET /v1/roles. System roles first, then the rest by name. */
export type RoleListResponse = { roles: WorkspaceRole[] };

/** GET, POST, PATCH /v1/roles/:id, POST /v1/roles/:id/copy. */
export type RoleResponse = { role: WorkspaceRole };

/**
 * POST /v1/roles. `permissions` may leave a key out, which reads as off. A
 * permission that needs another (`needsOf`) is refused while that one is off.
 */
export type RoleCreateRequest = {
  name: string;
  purpose?: string | null;
  permissions: Partial<RolePermissions>;
};

/**
 * PATCH /v1/roles/:id. An absent field is left alone. `permissions` is the
 * whole set when present, with a missing key read as off, and `seesNewRecords`
 * sets that one switch on its own.
 */
export type RolePatchRequest = {
  name?: string;
  purpose?: string | null;
  permissions?: Partial<RolePermissions>;
  seesNewRecords?: boolean;
};

/** POST /v1/roles/:id/copy: a made role with the same permissions, named "<name> (copy)" unless named here. */
export type RoleCopyRequest = { name?: string };

/** DELETE /v1/roles/:id?moveTo=<role id>. */
export type RoleRemoveResponse = { removed: true; moved: number };

/** One permission a change moves: on to off, or a reach to a narrower or wider one. */
export type PermissionMove = {
  key: PermissionKey;
  from: Reach | boolean;
  to: Reach | boolean;
};

/**
 * GET /v1/roles/:id/impact?permissions=<json>: who a change to this role
 * reaches before it is saved. Without `permissions`, the holders alone.
 *
 * `quiet` is what goes quiet and is kept (Decided 6): the permissions turned
 * off or narrowed, and for each holder the records whose access sits above
 * the new ceiling. Nothing is deleted; raising the role again brings it back.
 */
export type RoleImpactResponse = {
  roleId: string;
  count: number;
  people: Array<{ userId: string; name: string | null; email: string }>;
  lowered: PermissionMove[];
  raised: PermissionMove[];
  quiet: Array<{ userId: string; recordId: string; recordName: string; fields: number; lists: number; record: boolean }>;
};

/* ----------------------------------------------------------------- people */

/** One person on Team. */
export type WorkspacePerson = {
  userId: string;
  /** The name this workspace knows them by, else their account's; null when neither. */
  name: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  roleId: string | null;
  roleName: string;
  systemKey: SystemRoleKey | null;
  /** The primary admin: the owner, with the crown. */
  isPrimary: boolean;
  /** Whether the role decides what they may do yet (phase 7 makes it true for everyone). */
  roleLive: boolean;
  /** The user id of the person they report to. */
  reportsTo: string | null;
  /** How many people are below them, however deep. */
  teamSize: number;
  lastActiveAt: string | null;
  joinedAt: string;
  /** What they can open, for Team's Can open column; absent from a server before web-redesign-03. */
  opens?: PersonOpens;
};

/**
 * What a person can open, as Team lists it: every record (the primary admin,
 * and an Admin once their role is live), or the records and lists they hold
 * access to. Their page reads the detail (`PersonAccessResponse`).
 */
export type PersonOpens = { everything: boolean; recordIds: string[] };

/** GET /v1/members. Everyone, for a person whose role holds See the team; only themselves otherwise. */
export type PeopleListResponse = { people: WorkspacePerson[] };

/** PATCH /v1/members/:userId. An absent field is left alone; null clears. */
export type PersonPatchRequest = {
  roleId?: string;
  reportsTo?: string | null;
  phone?: string | null;
  address?: string | null;
  name?: string | null;
};

export type PersonResponse = { person: WorkspacePerson };

/** GET /v1/members/:userId/team: everyone below them, however deep. `depth` 1 reports to them directly. */
export type PersonTeamResponse = { team: Array<WorkspacePerson & { depth: number }> };

/** Whom a removed person's team now reports to; null when nobody. */
export type TeamMovedTo = { userId: string; name: string | null } | null;

/** DELETE /v1/members/:userId. Their team moves up to their manager (Decided 9). */
export type PersonRemoveResponse = {
  removed: true;
  grantsRevoked: number;
  entriesKept: number;
  /** Their direct reports, who now report to `teamMovedTo`; people further below keep their managers. */
  teamMoved: number;
  teamMovedTo: TeamMovedTo;
};

/* ----------------------------------------------------------------- access */

/** Access to a whole record: See or Change. None is no access at all. */
export type RecordAccessLevel = 'see' | 'change';

/** Who gave a person their access. Null when a job, a template or the old routes wrote it. */
export type AccessGiver = { userId: string; name: string | null } | null;

/** One field of a record, for one person. */
export type FieldAccessRow = {
  fieldId: string;
  name: string;
  /** What applies: the stored value, or what the record sets, cut to the role's ceiling. */
  access: AccessLevel;
  /** What access set for this field on its own; null when it follows the record. */
  set: AccessLevel | null;
  /** Set above what the role allows: kept, and drawn greyed. */
  quiet: boolean;
  /** The form needs it to save an entry: never None for somebody who adds. Absent before web-redesign-03 phase 4. */
  required?: boolean;
};

/** One related list of a record, for one person. */
export type ListAccessRow = {
  recordId: string;
  name: string;
  access: AccessLevel;
  set: AccessLevel | null;
  quiet: boolean;
};

/** One record or list, for one person. */
export type RecordAccessRow = {
  recordId: string;
  name: string;
  kind: VisibleRecordKind;
  /** What applies here; none when it was not given. */
  access: RecordAccessLevel | 'none';
  /** Some field is set on its own: the record reads Mixed. */
  mixed: boolean;
  /** The most their role allows in any record: Change when it adds or changes entries. */
  ceiling: RecordAccessLevel;
  /**
   * Their role adds entries, so a field the form needs is never None or See at
   * Change (FORM_NEEDS). Absent from a server before roles-parity phase 5.
   */
  adds?: boolean;
  /** The access sets more than the role allows. */
  quiet: boolean;
  /** Whose entries each entry permission reaches here; null when not given. */
  reach: EntryReaches | null;
  fields: FieldAccessRow[];
  lists: ListAccessRow[];
  givenBy: AccessGiver;
  givenAt: string | null;
  /** The job that wrote this access, until phase 7 folds jobs in. */
  viaJob: string | null;
};

/** GET /v1/members/:userId/access: every record and list in the workspace. */
export type PersonAccessResponse = {
  userId: string;
  roleId: string | null;
  roleName: string;
  /** The primary admin and every Admin open everything; access is never set for them. */
  opensEverything: boolean;
  records: RecordAccessRow[];
};

/**
 * What a write sets on one record for one person. `access: 'none'` takes the
 * record away. Fields and lists left out follow the record; a field or list
 * named sets its own value.
 */
export type AccessSet = {
  access: RecordAccessLevel | 'none';
  fields?: Record<string, AccessLevel>;
  lists?: Record<string, AccessLevel>;
};

/** PUT /v1/members/:userId/access. Records not named are left as they are. */
export type PersonAccessPutRequest = { records: Array<AccessSet & { recordId: string }> };

/** GET /v1/records/:recordId/access: every person in the workspace. */
export type RecordAccessResponse = {
  recordId: string;
  name: string;
  people: Array<
    Omit<RecordAccessRow, 'recordId' | 'name' | 'kind'> & {
      userId: string;
      personName: string | null;
      email: string;
      roleId: string | null;
      roleName: string;
      opensEverything: boolean;
    }
  >;
};

/** PUT /v1/records/:recordId/access. People not named are left as they are. */
export type RecordAccessPutRequest = { people: Array<AccessSet & { userId: string }> };

/** Access to one record at one moment, for the save dialog. */
export type AccessSnapshot = {
  access: RecordAccessLevel;
  fields: Record<string, AccessLevel>;
  lists: Record<string, AccessLevel>;
};

/** One thing a save changed: before and after, null when there was no access. */
export type AccessChange = {
  userId: string;
  recordId: string;
  recordName: string;
  before: AccessSnapshot | null;
  after: AccessSnapshot | null;
};

/** Both access PUTs answer what changed for each person. Unchanged records are left out. */
export type AccessPutResponse = { changes: AccessChange[] };

/* ---------------------------------------------------------------- invites */

/** POST /v1/invites (Decided 10). One of email and phone is needed. */
export type PersonInviteRequest = {
  name: string;
  email?: string | null;
  phone?: string | null;
  roleId: string;
  access?: Array<AccessSet & { recordId: string }>;
  reportsTo?: string | null;
  delivery: InviteDelivery;
};

/** One invite on Team's Invited tab. */
export type PersonInvite = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  roleId: string;
  roleName: string;
  /** How many records and lists it opens. */
  canOpen: number;
  reportsTo: string | null;
  delivery: InviteDelivery;
  createdBy: string;
  sentAt: string;
  expiresAt: string;
  expired: boolean;
  /** The link to hand over. It carries the invite's bearer token. */
  link: string | null;
};

/** GET /v1/invites: the invites not yet used or withdrawn. */
export type PersonInviteListResponse = { invites: PersonInvite[] };

/**
 * POST /v1/invites and POST /v1/invites/:id/resend. `code` is shown once and
 * never stored: a resend makes a new one. `emailed` is false for a phone
 * invite, and when the email could not be sent.
 */
export type PersonInviteSentResponse = {
  invite: PersonInvite;
  code: string | null;
  link: string | null;
  emailed: boolean;
};

/* ------------------------------------------------------------------- asks */

export type AskKind = 'right' | 'primary_admin' | 'handover';

/** One open ask, as Team's asks list it. */
export type WorkspaceAsk = {
  /** The id to answer it by. */
  id: string;
  kind: AskKind;
  from: { userId: string; name: string | null; email: string };
  /** What a right ask asks for; null for the other kinds. */
  right: RightAsk['right'] | null;
  note: string | null;
  /** For a hand-over: to whom, and the role the primary admin takes. */
  handover: { toUserId: string; keepRoleId: string } | null;
  createdAt: string;
};

/** GET /v1/asks: every open ask, for a person who may answer; their own otherwise. */
export type AskListResponse = { asks: WorkspaceAsk[] };

/** POST /v1/asks. */
export type AskCreateRequest = { right: RightAsk['right']; note?: string | null };
export type AskCreateResponse = { ask: WorkspaceAsk };

/**
 * POST /v1/asks/:id/answer. Granting a permission gives the person a role
 * that holds it (`roleId`); granting a record gives them that access.
 */
export type AskAnswerRequest = { answer: 'grant' | 'dismiss'; roleId?: string };
export type AskAnswerResponse = { answered: 'granted' | 'dismissed' };

/* -------------------------------------------------------- the primary admin */

/** POST /v1/workspace/primary/handover: to a person, and the role the primary admin takes. */
export type PrimaryHandoverRequest = { toUserId: string; keepRoleId: string };
export type PrimaryHandoverResponse = { ask: WorkspaceAsk };

/**
 * POST /v1/workspace/primary/ask: anyone but the primary admin asks to become
 * it (web-redesign-03 phase 7.3). Asking twice while one is open answers the
 * open one. The primary admin answers it by handing over, or puts it away.
 */
export type PrimaryAskResponse = { ask: WorkspaceAsk };

/**
 * POST /v1/workspace/primary/handover/accept. `askId`, when sent, is the offer
 * the caller means: the route accepts only that one, so an offer made since on
 * other terms is not taken for it (HANDOVER_NOT_FOUND). Without it, the offer
 * waiting on the caller.
 */
export type PrimaryAcceptRequest = { askId?: string };
export type PrimaryAcceptResponse = { primary: string; formerPrimary: string; formerRoleId: string };

/* --------------------------------------------------------------- activity */

export type ActivityFilter = 'all' | 'entries' | 'team' | 'access';

export const ACTIVITY_FILTERS: readonly ActivityFilter[] = ['all', 'entries', 'team', 'access'];

/** What app.member_event records. */
export type MemberEventKind = 'joined' | 'role' | 'access' | 'reports_to' | 'details' | 'invite' | 'removed' | 'primary';

export type ActivityItem =
  | {
      kind: 'entry';
      at: string;
      recordId: string;
      recordName: string;
      entryId: string;
      version: number;
      action: 'added' | 'changed' | 'removed';
    }
  | {
      kind: 'event';
      at: string;
      event: MemberEventKind;
      actor: { userId: string; name: string | null } | null;
      detail: Record<string, unknown>;
    };

export type ActivityDay = { day: string; items: ActivityItem[] };

/**
 * GET /v1/members/:userId/activity?filter=all|entries|team|access&before=<iso>.
 * Newest first, by day in the reader's time zone. `next` is the `before` of
 * the next page, null at the end.
 */
export type PersonActivityResponse = { days: ActivityDay[]; next: string | null };

/* -------------------------------------------------------------- workspace */

/** DELETE /v1/businesses/:id, the primary admin's, once nobody else is in it. */
/** `issuedDocuments`: how many issued documents went with it (D9 of report-templates.md). Optional for an older server. */
export type BusinessDeleteResponse = { deleted: true; issuedDocuments?: number };
